// libs
import cx from "classnames";
import React, { ReactElement, useCallback, useState, useRef, RefObject, useEffect, useMemo } from "react";

// components
import { AriaHidden, BottomSheet, BottomSheetChildrenProps } from "@swiggy-private/core-ui";
import { GenericAlertProps } from "@swiggy-private/cart-types";

// widgets
import { Button, BUTTON_ACCENT } from "@swiggy-private/core-ui";

// utils
import { useFocusTrap, getCloudinaryUrl } from "@swiggy-private/core-ui";

// test-ids
import { GENERIC_ALERT_TEST_IDS } from "@swiggy-private/cart-test-ids";

//hooks
import { useFocusDialog } from "@swiggy-private/core-ui";
import { getActiveElement } from "@swiggy-private/core-ui";

//styles
import styles from "./GenericAlert.scss";

const ICON_PAYMENT_ALERT_V4 = "PaymentLogos/paymentIcons/error";

type AlertContentProps = Omit<GenericAlertProps, "hideOnOverlayClick" | "onClose"> & {
    setAction: (fn: VoidFunction) => void;
};

const AlertContentComponent = React.memo(function AlertContent(
    props: AlertContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    const {
        setAction,
        primaryAction,
        secondaryAction,
        close,
        hideIcon,
        renderPrimaryCTA: renderPrimaryCTAProp,
    } = props;
    const iconUrl = getCloudinaryUrl(props.icon ?? ICON_PAYMENT_ALERT_V4, {
        trim: true,
        width: 64,
    });
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const handlePrimaryActionClick = useCallback((): void => {
        setAction(primaryAction.onClick);
        close();
    }, [close, primaryAction.onClick, setAction]);

    const handleSecondaryActionClick = useCallback((): void => {
        if (!secondaryAction) {
            close();
            return;
        }

        // setting after close action to secondary action handler
        setAction(secondaryAction.onClick);
        close();
    }, [close, secondaryAction, setAction]);

    const actionClass = cx(styles.genericAlertAction, {
        [styles.genericAlertActionSingle]: !props.secondaryAction,
        [styles.genericAlertActionPrimaryTaller]: props.primaryAction.subText,
    });

    const renderPrimaryCTA = useMemo((): ReactElement => {
        const buttonProps = {
            onClick: handlePrimaryActionClick,
            accent: BUTTON_ACCENT.SWIGGY_ORANGE_LARGE,
            className: actionClass,
        };

        if (renderPrimaryCTAProp && typeof renderPrimaryCTAProp === "function") {
            return renderPrimaryCTAProp(buttonProps);
        }

        return (
            <Button {...buttonProps} dataTestId={GENERIC_ALERT_TEST_IDS.PRIMARY_ACTION_BUTTON}>
                <div>{props.primaryAction.text}</div>
                {props.primaryAction.subText && (
                    <div className={styles.genericAlertActionSub}>{props.primaryAction.subText}</div>
                )}
            </Button>
        );
    }, [actionClass, handlePrimaryActionClick, props.primaryAction, renderPrimaryCTAProp]);

    return (
        <div>
            <div className={styles.genericAlertHeader} data-testid={GENERIC_ALERT_TEST_IDS.HEADER}>
                <div className={styles.genericAlertHeaderInfo}>
                    {props.info && (
                        <div className={styles.genericAlertContentRow}>
                            <span className={styles.genericAlertInfo} data-testid={GENERIC_ALERT_TEST_IDS.ALERT_INFO}>
                                {props.info} {props.subInfo && props.subInfo}
                            </span>
                        </div>
                    )}
                    <div className={styles.genericAlertHeaderContainer}>
                        <div>
                            {!hideIcon && (
                                <AriaHidden
                                    as="div"
                                    className={styles.genericAlertIcon}
                                    data-testid={GENERIC_ALERT_TEST_IDS.HEADER_ICON}
                                >
                                    <img alt={""} crossOrigin="anonymous" className={styles.iconImg} src={iconUrl} />
                                </AriaHidden>
                            )}
                        </div>
                        <div>
                            <div
                                className={styles.genericAlertHeaderTitle}
                                data-testid={GENERIC_ALERT_TEST_IDS.HEADER_TITLE}
                                ref={headerDivRef as RefObject<HTMLDivElement>}
                                tabIndex={0}
                                aria-level={3}
                            >
                                {props.title || ""}
                            </div>
                            <div
                                className={styles.genericAlertHeaderMessage}
                                data-testid={GENERIC_ALERT_TEST_IDS.HEADER_MESSAGE}
                            >
                                {props.message || ""}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.genericAlertContent} data-testid={GENERIC_ALERT_TEST_IDS.CONTENT}>
                <div className={styles.genericAlertContentRow}>{renderPrimaryCTA}</div>

                {props.secondaryAction && (
                    <div className={styles.genericAlertContentRow}>
                        <Button
                            onClick={handleSecondaryActionClick}
                            accent={BUTTON_ACCENT.SWIGGY_ORANGE_PLAIN}
                            className={cx(styles.genericAlertAction, styles.genericAlertActionSecondary)}
                            dataTestId={GENERIC_ALERT_TEST_IDS.SECONDARY_ACTION_BUTTON}
                        >
                            <div>{props.secondaryAction.text}</div>
                            {props.secondaryAction.subText && (
                                <div className={styles.genericAlertActionSub}>{props.secondaryAction.subText}</div>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
});

function GeneticAlert(props: GenericAlertProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, hideCloseIcon = false, closeButtonDataTestId, ..._props } = props;
    const [actionOnClose, setActionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    useFocusTrap();
    return (
        <BottomSheet
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={_onClose}
            hideCloseIcon={hideCloseIcon}
            closeButtonDataTestId={closeButtonDataTestId}
        >
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <AlertContentComponent {...bProps} {..._props} setAction={setActionOnClose} />
            )}
        </BottomSheet>
    );
}

export default React.memo(GeneticAlert);
